<template>
	<section v-if="sections.length > 0" class="location-closest-branches">
		<div class="location-closest-branches__content">
			<div v-for="section in sections" :key="section.type" class="location-closest-branches__section">
				<h2 class="location-closest-branches__title">
					{{ $t(section.titleKey) }}
				</h2>
				<div class="location-closest-branches__locations">
					<NuxtLink
						v-for="location in section.locations"
						:key="location.id"
						class="location-closest-branches__location"
						:to="getURLforLocation(location)">
						<div class="location-closest-branches__location-name-box">
							<div class="location-closest-branches__location-name">{{ locationLabel(location) }}</div>
							<div class="location-closest-branches__location-subtitle">{{ location[`subtitle${locale?.toUpperCase()}`] }}</div>
						</div>
						<GlobalIcon name="location-bold" :height="20" color="var(--primary-color)"></GlobalIcon>
					</NuxtLink>
				</div>
			</div>
			<ButtonPrimary :to="localePath('locations')" class="location-closest-branches__btn">
				{{ $t('modules.location_module.location.closest_branches.check_all_places') }}
			</ButtonPrimary>
		</div>
	</section>
</template>

<script setup lang="ts">
type TLocationBucketType = 'branch' | 'airport' | 'other' | 'delivery-bus' | 'passenger-bus'

interface ILocationSection {
	type: 'city' | 'tail'
	titleKey: string
	locations: ICMSLocationModel[]
}

// fixed relative order for same-city buckets; the current page's own bucket is moved to the front
const BUCKET_ORDER: TLocationBucketType[] = ['branch', 'airport', 'other', 'delivery-bus', 'passenger-bus']

const { locale, t } = useI18n()
const localePath = useLocalePath()

const cmsLocation = inject<Ref<ICMSLocationModel | undefined>>('cmsLocation') || ref<ICMSLocationModel | undefined>(undefined)
const sameCityLocations = inject<Ref<ICMSLocationModel[]>>('sameCityLocations') || ref<ICMSLocationModel[]>([])
const closestLocations = inject<Ref<ICMSLocationModel[]>>('closestLocations') || ref<ICMSLocationModel[]>([])

// mirrors the classification rules from src/pages/locations/index.vue
const classifyLocation = (location: ICMSLocationModel): TLocationBucketType => {
	if (location.otherLocation) return 'other'
	if (location.deliveryBus) return 'delivery-bus'
	if (location.passengerBus) return 'passenger-bus'
	if (location.icon === 'airplane') return 'airport'
	return 'branch'
}

const CLOSEST_BRANCHES_KEY = 'modules.location_module.location.closest_branches'

const locationLabel = (location: ICMSLocationModel) => {
	const name = location[`name${locale.value.toUpperCase()}`] ?? location.namePL

	if (location.passengerBus) return t(`${CLOSEST_BRANCHES_KEY}.passenger_bus_location`, { city: name })
	if (location.deliveryBus) return t(`${CLOSEST_BRANCHES_KEY}.delivery_bus_location`, { city: name })
	return name
}

const sortByName = (a: ICMSLocationModel, b: ICMSLocationModel) => {
	const loc = locale.value.toUpperCase() as 'PL' | 'EN'
	const nameA = (a[`name${loc}`] ?? a.namePL).toLowerCase()
	const nameB = (b[`name${loc}`] ?? b.namePL).toLowerCase()
	return nameA.localeCompare(nameB)
}

// one flat, ordered list: the current page's own bucket first, then the rest in a fixed order,
// each bucket sorted by name
const cityLocations = computed<ICMSLocationModel[]>(() => {
	if (!cmsLocation.value) return []

	const otherCityLocations = sameCityLocations.value.filter((location) => location.id !== cmsLocation.value?.id)
	if (otherCityLocations.length === 0) return []

	const ownType = classifyLocation(cmsLocation.value)
	const order = [ownType, ...BUCKET_ORDER.filter((type) => type !== ownType)]

	return order.flatMap((type) => otherCityLocations.filter((location) => classifyLocation(location) === type).sort(sortByName))
})

const sections = computed<ILocationSection[]>(() => {
	const result: ILocationSection[] = []

	if (cityLocations.value.length > 0) {
		result.push({
			type: 'city',
			titleKey: 'modules.location_module.location.closest_branches.same_city_locations',
			locations: cityLocations.value,
		})
	}

	if (closestLocations.value.length > 0) {
		result.push({
			type: 'tail',
			titleKey: 'modules.location_module.location.closest_branches.other_locations_in_the_region',
			locations: closestLocations.value,
		})
	}

	return result
})

const getURLforLocation = (item: ICMSLocationModel) => {
	const urlLocale = item[`isActive${locale.value.toUpperCase()}`] ? item[`url${locale.value.toUpperCase()}`] : undefined
	const url = item.urlPL

	if (!url && !urlLocale) return undefined
	else
		return urlLocale
			? localePath({ name: 'car-rental-urlSlug', params: { urlSlug: urlLocale } }, locale.value)
			: localePath({ name: 'car-rental-urlSlug', params: { urlSlug: url } }, 'pl')
}
</script>

<style lang="scss" scoped src="./ClosestBranches.scss"></style>
